import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { DEFAULT_LOCALE, isSupportedLocale, stripLocaleFromPath } from './localeConfig';

/**
 * Route wrapper that:
 * 1. Reads :lang param from URL
 * 2. Syncs i18n language with URL locale (URL -> i18n)
 * 3. Syncs URL locale with i18n language when user changes language (i18n -> URL)
 * 4. Redirects to /:defaultLang/... if locale is missing or invalid
 *
 * Used as element for the top-level `/:lang` route.
 */
export const LocaleSync: FC = () => {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // URL -> i18n: sync i18n language when URL locale changes
  useEffect(() => {
    if (!lang || !isSupportedLocale(lang)) {
      // Invalid or missing locale - redirect to current i18n language.
      // Ścieżkę bierzemy z routera i najpierw ZDEJMUJEMY z niej prefiks: efekt potrafi
      // wykonać się drugi raz (StrictMode) już po przekierowaniu i wtedy doklejenie
      // wprost dałoby `/pl/pl/...`. Query i hash muszą przetrwać — bez nich ginie np.
      // `?q=` z linku do wyszukanego zamówienia.
      const targetLocale = isSupportedLocale(i18n.language) ? i18n.language : DEFAULT_LOCALE;
      const path = stripLocaleFromPath(location.pathname);
      navigate(`/${targetLocale}${path === '/' ? '' : path}${location.search}${location.hash}`, { replace: true });
      return;
    }

    // Sync i18n language with URL locale
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n, navigate, location.pathname, location.search, location.hash]);

  // i18n -> URL: update URL when user changes language (e.g. via LanguageSwitcher)
  useEffect(() => {
    const handleLanguageChanged = (newLang: string) => {
      if (isSupportedLocale(newLang) && newLang !== lang) {
        const pathWithoutLocale = stripLocaleFromPath(window.location.pathname);
        const search = window.location.search || '';
        const hash = window.location.hash || '';
        navigate(`/${newLang}${pathWithoutLocale}${search}${hash}`, { replace: true });
      }
    };

    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n, lang, navigate]);

  return <Outlet />;
};
